import React, { useMemo } from "react";
import { LinkedAccount } from "../../../../../models/Entities";
import {
  MustUseAutoWebhookSetupNoticeCard,
  ThirdPartyWebhooksDisabledDueToSelectiveSyncFiltersNoticeCard,
  ThirdPartyWebhooksUpsellNoticeCard,
} from "../../../configuration/webhooks/WebhooksRestrictionsNotices";
import useProductRestrictions from "../../../../shared/hooks/useProductRestrictions";
import { SectionHeaderWrapper } from "../../../../shared/SectionHeaderWrapper";
import WebhookEventsTable from "./WebhookEventsTable";
import useAppContext from "../../../../context/useAppContext";
import MaybeDisable from "../../../../shared/MaybeDisable";
import {
  DOCS_WEBHOOKS_PATH,
  MERGE_QUICKBOOKS_WEBHOOKS_HELP_CENTER,
  MERGE_XERO_WEBHOOKS_HELP_CENTER,
} from "../../../../../router/RouterUtils";
import useLoadLinkedAccountSyncSelections from "../selective-sync/hooks/useLoadLinkedAccountSyncSelections";

type Props = {
  linkedAccount: LinkedAccount;
};
const WebhooksPage = ({ linkedAccount }: Props) => {
  // hooks
  const { user } = useAppContext();
  const { productRestrictions } = useProductRestrictions();

  // derived state
  const integrationName = useMemo(() => linkedAccount?.integration.name ?? "", [linkedAccount]);
  const isUsingAutoWebhookSetup = Boolean(
    user.organization.enable_auto_webhook_setup &&
      linkedAccount?.integration?.is_auto_webhook_setup_enabled,
  );

  const getWebhookDocsURL = (integrationName: string) => {
    switch (integrationName) {
      case "QuickBooks Online":
        return MERGE_QUICKBOOKS_WEBHOOKS_HELP_CENTER;
      case "Xero":
        return MERGE_XERO_WEBHOOKS_HELP_CENTER;
      default:
        return DOCS_WEBHOOKS_PATH;
    }
  };

  const isPlanWithThirdPartyWebhooks = Boolean(productRestrictions?.third_party_webhooks_enabled);

  const { linkedAccountSyncSelections } = useLoadLinkedAccountSyncSelections({
    linkedAccount,
  });
  const hasLinkedAccountSyncSelections =
    linkedAccountSyncSelections && linkedAccountSyncSelections.length > 0;

  // If the integration supports Auto Webhook Setup then manual setup is not supported.
  const showMustEnableAutoWebhookSetup =
    isPlanWithThirdPartyWebhooks &&
    linkedAccount?.integration.is_auto_webhook_setup_enabled &&
    !user.organization.enable_auto_webhook_setup;

  const isWebhookEventsTableDisabled =
    !isPlanWithThirdPartyWebhooks ||
    showMustEnableAutoWebhookSetup ||
    (hasLinkedAccountSyncSelections &&
      !user.is_file_storage_selective_sync_webhooks_override_enabled);

  // If webhooks are disabled in ProductRestrictions, then we show an upsell to upgrade their organization's plan.
  // Otherwise, if the integration only supports auto webhook creation but the customer
  // doesn't have that setting enabled, then instruct them on how to do so.
  const webhooksUpsellOrEnableSetting = !isPlanWithThirdPartyWebhooks ? (
    <div style={{ marginBottom: "36px" }}>
      <ThirdPartyWebhooksUpsellNoticeCard />
    </div>
  ) : showMustEnableAutoWebhookSetup && !hasLinkedAccountSyncSelections ? (
    <div style={{ marginBottom: "36px" }}>
      <MustUseAutoWebhookSetupNoticeCard integrationName={integrationName} />
    </div>
  ) : null;

  const webhooksDisabledDueToSelectiveSyncFilters =
    isPlanWithThirdPartyWebhooks &&
    hasLinkedAccountSyncSelections &&
    !user.is_file_storage_selective_sync_webhooks_override_enabled ? (
      <div className=" mb-9">
        <ThirdPartyWebhooksDisabledDueToSelectiveSyncFiltersNoticeCard
          path={window.location.pathname.replace("webhooks", "selective-sync")}
        />
      </div>
    ) : null;

  if (!linkedAccount) return null;

  // Show detailed info if there's an upsell for webhooks to make the page more full
  return (
    <SectionHeaderWrapper
      title="Webhooks"
      subtitle={
        !hasLinkedAccountSyncSelections
          ? `Configure ${integrationName} to send real-time data updates to Merge for this Linked
              Account`
          : undefined
      }
      docsUrl={getWebhookDocsURL(linkedAccount?.integration.name)}
    >
      <>
        {webhooksUpsellOrEnableSetting}
        {webhooksDisabledDueToSelectiveSyncFilters}
        <MaybeDisable disable={isWebhookEventsTableDisabled}>
          <WebhookEventsTable
            integrationName={integrationName}
            linkedAccountId={linkedAccount.id}
            isUsingAutoWebhookSetup={isUsingAutoWebhookSetup}
          />
        </MaybeDisable>
      </>
    </SectionHeaderWrapper>
  );
};

export default WebhooksPage;
